import { fabric } from "fabric";
import { v4 } from "uuid";
import { CanvasHandler } from "../../handlers";
import { FabricEvent, FabricObject } from "../../types/FabricCustomType";
import { IObjectDesingner } from "../../types/HandlerObjectType";
import RectTableProperties from "./properties/RectTableProperties";
import { ObjectSuperTypesEnum, ObjectTypesEnum, SeatDistribution } from "../../enums";
import { TextFactory } from "./factories";
import store from "../../redux/store";
import { RectTableSeatHelper } from "./helpers/RectTableSeatHelper";

interface RecTableSeats {
  up: number;
  down: number;
  left: number;
  right: number;
}

interface RectTableDimensions {
  width: number;
  height: number;
}
class RectTable extends RectTableProperties implements IObjectDesingner {

  readonly INIT_DEFAULT_SEAT_COLOR = "#000000";
  readonly SEAT_DIAMETER = 15;
  readonly INIT_SEAT_GAP = 8;

  private rectTableSeats: RecTableSeats;
  private rectTableDimensions: RectTableDimensions;

  

  constructor(canvasHandler: CanvasHandler) {
    super(canvasHandler);
  }

  public onInit() {
    this.canvasHandler.interactionHandler.drawing(ObjectSuperTypesEnum.RectTable, "crosshair");
    this.canvasHandler.activeShape = null;
  }
  
  onRotated(event: FabricEvent) {
    throw new Error("Method not implemented.");
  }

  onMouseDblClick(event: FabricEvent<Event>) {
    return;
  }

  public onMousedown(event: FabricEvent<MouseEvent>) {
    const {
      properties: { rectTableSeats, rectTableDimensions },
    } = store.getState();
    this.rectTableSeats = rectTableSeats;
    this.rectTableDimensions = rectTableDimensions;
    const pointer = this.canvasHandler.canvas.getPointer(event.e, true);
    this.generateRectTable(pointer.x, pointer.y);
  }

  public onMousemove(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    return;
  }

  public onMouseup(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    return;
  }

  private createTableGroup() {
    this.canvasHandler.isDown = true;

    const tableGroup = new fabric.Group(undefined, {
      left: 0,
      top: 0,
      originX: "center",
      originY: "center",
      hasControls: true,
      hasBorders: true,
      selectable: true,
      type: ObjectTypesEnum.Group,
      subTargetCheck: true,
    }) as FabricObject<fabric.Group>;

    tableGroup.set({
      id: v4(),
      superType: ObjectSuperTypesEnum.Table,
      category: {
        key: 0,
        name: "",
        color: ""
      },
      label: "",
      displayedLabel: "",
      sectionLabel: "",
      value: "",
    });

    const rect = new fabric.Rect({
      left: 0,
      top: 0,
      originX: "center",
      originY: "center",
      width: this.rectTableDimensions.width,
      height: this.rectTableDimensions.height,
      angle: 0,
      fill: "#D1D1D1",
      stroke: "#A1A1A1",
      strokeWidth: 1,
      strokeUniform: true,
      transparentCorners: false,
      rx: 1,
      ry: 1,
      opacity: 1,
      objectCaching: !this.canvasHandler.editable,
      type: ObjectTypesEnum.Rect,
      hasControls: false,
      hasBorders: false,
      selectable: true,
      lockMovementX: true,
      lockMovementY: true,
      hoverCursor: "pointer",
    }) as FabricObject<fabric.Rect>;

    rect.set({
      id: v4(),
      label: "",
      displayedLabel: "",
      container: this.canvasHandler.container.id,
      editable: false,
      objectOption: this.canvasHandler.objectOption,
      opacity: 1
    });

    const text = TextFactory.create({left: 0, top: 0, visible: false, fontSize: 16});

    tableGroup.addWithUpdate(rect);
    tableGroup.addWithUpdate(text);
    return tableGroup;
  }

  private createRectTableGroup(x: number, y: number) {
    const rectTableGroup = new fabric.Group(undefined, {
      left: 0,
      top: 0,
      originX: "center",
      originY: "center",
      hasControls: true,
      hasBorders: true,
      selectable: true,
      type: ObjectTypesEnum.Group,
      subTargetCheck: true,
    }) as FabricObject<fabric.Group>;

    rectTableGroup.setControlsVisibility({
      tl: false, //top-left
      mt: false, // middle-top
      tr: false, //top-right
      ml: false, //middle-left
      mr: false, //middle-right
      bl: false, // bottom-left
      mb: false, //middle-bottom
      br: false, //bottom-right
      mtr: true,
    });

    rectTableGroup.set({
      id: v4(),
      superType: ObjectSuperTypesEnum.RectTable,
      selectionType: 1,
      category: {
        key: 0,
        name: "",
        color: "#000000"
      },
      seatsQuantity:{
        left: this.rectTableSeats.left,
        right: this.rectTableSeats.right,
        up: this.rectTableSeats.up,
        down: this.rectTableSeats.down
      },
      section: {
        label: "",
        displayedLabel: "",
      },
      tableLabeling:{
        visible: false,
        label: "",
        displayedLabel: ""
      },
      seatLabeling:{
        key: SeatDistribution.NONE,
        startsAt: 1,
        list: []
      }
    });
    return rectTableGroup;
  }

  private async generateRectTable(x: number, y: number) {
    const group = this.createRectTableGroup(0, 0);
    const table = this.createTableGroup();
    group.add(table);
    
    await this.initFillTableWithSeats(group, table);
    
    const maxSeats = RectTableSeatHelper.calculateMaxSeats({table, seatRadius: this.SEAT_DIAMETER/2});
    group.set(
      { left: x, 
        top: y,
        maxSeats,
        angleHelp: 0 }
    );

    group.setCoords();
    this.canvasHandler.canvas.add(group);
    this.canvasHandler.onAdd(group);
    if (!this.canvasHandler.transactionHandler.active) {
      this.canvasHandler.transactionHandler.save("add");
    }
    this.canvasHandler.isDown = false;
    this.canvasHandler.canvas.requestRenderAll();
  }

  private async initFillTableWithSeats(
    group: FabricObject<fabric.Group>,
    tableGroup: FabricObject<fabric.Group>
  ) {
    const table = tableGroup
      .getObjects()
      .find((obj: any) => obj.type === ObjectTypesEnum.Rect);

    if (!table) return;

    const positions = RectTableSeatHelper.getSeatPositions({
      seatsQuantity: group.seatsQuantity,
      table,
      tableGroup,
      seatRadius: this.SEAT_DIAMETER / 2,
      seatGap: this.INIT_SEAT_GAP,
    });

    const seats = await RectTableSeatHelper.createSeats({
      positions,
      rowSeatSize: this.SEAT_DIAMETER,
      category: group.category,
    });

    seats.forEach((seat: any) => {
      group.add(seat);
    });

    group.addWithUpdate();
  }
}

export default RectTable;
